import { IReinstateablePolicy, IUserAccount, IProductDetail } from "config";
import { AppError } from "config/errors";
import {
  ICoverageAdjustmentInput,
  requestCoverageAdjustment,
  requestReinstatement,
  requestNewBusiness,
} from "gql/mutations/policy";
import { getCoverageEditorStateQuery, ICoverageEditorStateInput } from "gql/queries/field";
import {
  fetchCurrentUserPolicyQuery,
  fetchReinstateablePolicies,
  policyDocumentsQuery,
  policyRidersQuery,
} from "gql/queries/policy";
import { RootState } from "store";
import { getIsLoggedIn } from "store/selectors";
import { getActiveAccount } from "utils/account";

import { createAsyncThunk } from "@reduxjs/toolkit";
import { every } from "lodash-es";

import { getLatestInsuranceApplication } from "./insuranceApplications";

/* ------------       THUNKS      ------------------ */

export const getCoverageEditorState = createAsyncThunk(
  "policy/requestPolicyCoverageAdjustment",
  async (payload: ICoverageEditorStateInput, { rejectWithValue }) => {
    try {
      const { data } = await getCoverageEditorStateQuery(payload);

      return data.coverageEditorState;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  },
  {
    condition: payload => {
      if (every(payload, value => !value)) {
        return false;
      }
    },
  }
);

export const getCurrentUserPolicy = createAsyncThunk(
  "policy/getCurrentUserPolicy",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await fetchCurrentUserPolicyQuery();
      const account = getActiveAccount(data.me) as IUserAccount;
      const activePolicy = account?.activePolicy;
      const latestPayableInvoice = account?.latestPayableInvoice;
      const coverageAdjustmentAvailable = account?.coverageAdjustmentAvailable;

      return { activePolicy, latestPayableInvoice, coverageAdjustmentAvailable };
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const getReinstateablePolicies = createAsyncThunk(
  "policy/getReinstateablePolicies",
  async (_, { rejectWithValue }) => {
    try {
      const {
        data: { policies },
      } = await fetchReinstateablePolicies();

      return policies;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const getReinstateablePoliciesPolling = createAsyncThunk(
  "policy/getReinstateablePolicyWithDetailsPolling",
  async (policyId: IReinstateablePolicy["id"], { dispatch, rejectWithValue }) => {
    try {
      const [policies, latestInsuranceApplication] = await Promise.all([
        dispatch(getReinstateablePolicies()).unwrap(),
        dispatch(getLatestInsuranceApplication()).unwrap(),
      ]);

      return {
        caseId: latestInsuranceApplication?.id,
        caseResult: latestInsuranceApplication?.result,
        productsOffering: policies.find(
          ({ id }: { id: IReinstateablePolicy["id"] }) => id === policyId
        )?.reinstatementRequest?.productsOffering,
      };
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  },
  { condition: policyId => !!policyId }
);

export const requestPolicyCoverageAdjustment = createAsyncThunk(
  "policy/requestPolicyCoverageAdjustment",
  async (payload: ICoverageAdjustmentInput, { rejectWithValue }) => {
    try {
      const { data } = await requestCoverageAdjustment(payload);
      const response = data.requestCoverageAdjustment.response;
      const underwritingRequired = response.status === "UNDERWRITING_REQUIRED" ? true : false;

      return { ...response, underwritingRequired };
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

interface IRequestPolicyNewBusiness {
  product: IProductDetail["slug"];
}

export const requestPolicyNewBusiness = createAsyncThunk(
  "policy/requestNewBusiness",
  async ({ product }: IRequestPolicyNewBusiness, { rejectWithValue }) => {
    try {
      const {
        data: {
          requestNewBusiness: { response: requestNewBusinessResponse },
        },
      } = await requestNewBusiness({ product });

      return requestNewBusinessResponse;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const isLoggedIn = getIsLoggedIn(state);

      return !!isLoggedIn;
    },
  }
);

export const requestPolicyReinstatement = createAsyncThunk(
  "policy/requestPolicyReinstatement",
  async (policyId: IReinstateablePolicy["id"], { rejectWithValue }) => {
    try {
      const {
        data: {
          requestReinstatement: { response: requestReinstatementResponse },
        },
      } = await requestReinstatement(policyId);

      return requestReinstatementResponse;
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const getPolicyRiders = createAsyncThunk(
  "policy/getPolicyRiders",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await policyRidersQuery();

      const account = getActiveAccount(data.me) as IUserAccount;
      const activePolicy = account?.activePolicy;

      return activePolicy?.policyRiders || [];
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);

export const getPolicyDocuments = createAsyncThunk(
  "policy/getPolicyDocuments",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await policyDocumentsQuery();

      const account = getActiveAccount(data.me);
      const activePolicy = account?.activePolicy;

      return activePolicy?.documents || [];
    } catch (err) {
      return rejectWithValue(AppError.from(err).toPlainObject());
    }
  }
);
